import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePortal } from '@/contexts/PortalContext';
import { 
  GraduationCap, 
  BookOpen, 
  Clock, 
  Users,
  CheckCircle,
  ChevronRight,
  Calendar,
  Award,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyProgram() {
  const { enrollments, courses, enrollInProgram, loading: portalLoading } = usePortal();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  // Fetch available programs
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await fetch(`${API_URL}/api/portal/programs`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setPrograms(data.programs || []);
        }
      } catch (err) {
        console.error('Failed to fetch programs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  const handleEnroll = async (programId) => {
    setEnrolling(true);
    const result = await enrollInProgram(programId);
    setEnrolling(false);
    
    if (!result.success) {
      alert(result.error || 'Failed to enroll');
    }
  };

  const isEnrolled = (programId) => {
    return enrollments.some(e => e.program_id === programId);
  };

  if (loading || portalLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A] mx-auto mb-4" />
          <p className="text-gray-500">Loading programs...</p>
        </div>
      </div>
    );
  }

  // If enrolled, show enrolled program details
  if (enrollments.length > 0) {
    const enrollment = enrollments[0]; // For now, show first enrollment
    const programCourses = courses.filter(c => c.program_id === enrollment.program_id);
    
    return (
      <div className="space-y-8" data-testid="my-program-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">My Program</h1>
          <p className="text-gray-500">Track your progress through the program</p>
        </div>

        {/* Program Overview Card */}
        <Card className="overflow-hidden">
          <div className="bg-gradient-to-r from-[#991B1B] to-[#B91C1C] p-6 lg:p-8 text-white">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-6 h-6" />
                  <span className="text-red-100 text-sm">Enrolled Program</span>
                </div>
                <h2 className="text-2xl font-bold mb-2">{enrollment.program_title}</h2>
                <p className="text-red-100">
                  {programCourses.length} Courses | {enrollment.total_lessons} Lessons
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold">{Math.round(enrollment.progress_percentage)}%</div>
                <div className="text-red-100 text-sm">Complete</div>
              </div>
            </div>
            <div className="mt-6">
              <Progress value={enrollment.progress_percentage} className="h-3 bg-white/20" />
              <p className="text-sm text-red-100 mt-2">
                {enrollment.completed_lessons} of {enrollment.total_lessons} lessons completed
              </p>
            </div>
          </div>
        </Card>

        {/* Program Courses */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Program Courses</h3>
          <div className="grid gap-4">
            {programCourses.map((course, index) => (
              <Link key={course.course_id} to={`/portal/courses/${course.course_id}`}>
                <Card className="hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4 lg:p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#1E3A8A] rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-[#1E3A8A] bg-blue-50 px-2 py-0.5 rounded">
                            {course.code}
                          </span>
                          {course.progress_percentage === 100 && (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                        <h4 className="font-semibold text-gray-900 mb-1">{course.title}</h4>
                        <p className="text-sm text-gray-500 line-clamp-2">{course.description}</p>
                        <div className="mt-3 flex items-center gap-4">
                          <div className="flex items-center gap-2 flex-1">
                            <Progress value={course.progress_percentage} className="h-2" />
                            <span className="text-sm font-medium text-gray-600 whitespace-nowrap">
                              {Math.round(course.progress_percentage)}%
                            </span>
                          </div>
                          <span className="text-sm text-gray-400">
                            {course.completed_lessons}/{course.total_lessons} lessons
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Program Completion Status */}
        {enrollment.progress_percentage === 100 && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <Award className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-green-800 mb-2">
                Congratulations!
              </h3>
              <p className="text-green-700">
                You have completed all courses in this program.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // If not enrolled, show available programs
  return (
    <div className="space-y-8" data-testid="my-program-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Available Programs</h1>
        <p className="text-gray-500">Browse and enroll in workforce intelligence programs</p>
      </div>

      {/* Programs List */}
      {programs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No programs available at this time</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {programs.map((program) => (
            <Card key={program.program_id} className="overflow-hidden">
              <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <GraduationCap className="w-5 h-5" />
                  <span className="text-blue-100 text-sm">{program.credential_type}</span>
                </div>
                <h2 className="text-xl font-bold">{program.title}</h2>
              </div>
              <CardContent className="p-6">
                <p className="text-gray-600 mb-4">{program.description}</p>
                
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{program.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{program.course_count} Courses</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{program.enrolled_count} Enrolled</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{program.credential_type}</span>
                  </div>
                </div>

                {program.learning_outcomes && program.learning_outcomes.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-2">What You'll Learn</h4>
                    <ul className="space-y-1">
                      {program.learning_outcomes.slice(0, 3).map((outcome, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Button 
                  className="w-full bg-[#1E3A8A] hover:bg-[#1E3A8A]/90"
                  onClick={() => handleEnroll(program.program_id)}
                  disabled={enrolling || isEnrolled(program.program_id)}
                >
                  {enrolling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enrolling...
                    </>
                  ) : isEnrolled(program.program_id) ? (
                    'Already Enrolled'
                  ) : (
                    'Enroll Now'
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
